import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Title, ActionIcon } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatCount, formatDateTime } from '../../lib/format';
import { StatusPill, type PillTone } from '../../components/panel';

type Site = flows.KbWebsite;
const MUTED = 'var(--rr-color-text-muted)';
const busy = (s: Site) => !!s.job || s.status === 'analysing' || s.status === 'reading';
const tone = (s: Site): PillTone => (s.status === 'failing' ? 'rejected' : busy(s) ? 'submitted' : s.status === 'ready' ? 'active' : 'paused');
const statusKey = (s: Site) =>
  s.job === 'analyse' || s.status === 'analysing' ? 'analysing' : s.job || s.status === 'reading' ? 'reading' : s.status;
const errorText = (e: unknown, fallback: string) => {
  const body = (e as { body?: { error?: { message?: string } } } | null)?.body?.error?.message;
  return body ?? (e instanceof Error ? e.message : fallback);
};

/**
 * Websites in the knowledge base (3 October 2026). The customer gives an
 * address; the site is analysed and the pages worth reading suggested within
 * what the plan has room for; the customer keeps or changes the choice and
 * reads it; and reads it again by hand when the site changes.
 */
export function WebsitesCard() {
  const t = useT();
  const qc = useQueryClient();
  const [address, setAddress] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['kb-websites'],
    queryFn: () => flows.listKbWebsites(),
    // While a site is being looked at, the card follows it.
    refetchInterval: (q) => ((q.state.data?.data ?? []).some(busy) ? 3000 : false)
  });
  const sites = list.data?.data ?? [];
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['kb-websites'] });
    void qc.invalidateQueries({ queryKey: ['kb-website'] });
    void qc.invalidateQueries({ queryKey: ['kb-documents'] });
  };

  const add = useMutation({
    mutationFn: () => flows.createKbWebsite({ url: address.trim() }),
    onSuccess: (res) => { setAddress(''); refresh(); setOpen(res.data.id); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.web.title'))
  });
  const readAgain = useMutation({
    mutationFn: (s: Site) => flows.readKbWebsite(s.id),
    onSuccess: () => { refresh(); notifySuccess(t('knowledge.web.toast.reading'), t('knowledge.web.title')); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.web.title'))
  });
  const remove = useMutation({
    mutationFn: (s: Site) => flows.deleteKbWebsite(s.id),
    onSuccess: (_r, s) => { refresh(); notifySuccess(t('knowledge.web.toast.removed'), s.name); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), t('knowledge.web.title'))
  });

  return (
    <Card withBorder data-testid="kb-websites">
      <Stack gap="sm">
        <Stack gap={2}>
          <Title order={4}>{t('knowledge.web.title')}</Title>
          <Text role="bodySmall" c={MUTED}>{t('knowledge.web.intro')}</Text>
        </Stack>
        <form onSubmit={(e) => { e.preventDefault(); if (address.trim()) add.mutate(); }}>
          <Group gap="xs" align="flex-end" wrap="wrap">
            <div style={{ flex: 1, minWidth: 220 }}>
              <TextInput
                label={t('knowledge.web.address')}
                description={t('knowledge.web.addressHint')}
                placeholder="www.example.co.za"
                value={address}
                onChange={(e) => setAddress(e.currentTarget.value)}
              />
            </div>
            <Button type="submit" size="sm" loading={add.isPending} disabled={!address.trim()}>{t('knowledge.web.analyse')}</Button>
          </Group>
        </form>
        {list.error && <Alert color="error" role="alert">{(list.error as Error).message}</Alert>}
        {!list.isPending && sites.length === 0 && <EmptyState title={t('knowledge.web.empty')} description={t('knowledge.web.emptyHint')} />}
        {sites.map((s) => (
          <div key={s.id} className="rr-knowledge__source" data-testid="kb-website">
            <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
              <Group gap="xs" wrap="wrap">
                <Text role="bodySmall" fw={650}>{s.name}</Text>
                <StatusPill tone={tone(s)}>{t(`knowledge.web.status.${statusKey(s)}`)}</StatusPill>
              </Group>
              <Text role="caption" c={MUTED}>
                {s.start_url}
                {s.read_finished_at
                  ? ` · ${t('knowledge.web.pagesIn', { count: formatCount(s.documents) })} · ${t('knowledge.web.lastRead', { when: formatDateTime(s.read_finished_at) })}`
                  : s.analysed_at ? ` · ${t('knowledge.web.foundLine', { found: formatCount(s.analysis.found ?? 0), suggested: formatCount(s.analysis.suggested ?? 0) })}` : ''}
              </Text>
              {(s.analysis.new_pages ?? 0) > 0 && !busy(s) && <Text role="caption">{t('knowledge.web.newPages', { count: formatCount(s.analysis.new_pages ?? 0) })}</Text>}
              {s.status === 'failing' && s.last_error && <Text role="caption" c="var(--rr-color-error, #dc2626)">{s.last_error}</Text>}
            </Stack>
            <Group gap={4} wrap="nowrap">
              <Button size="xs" variant="default" onClick={() => setOpen(s.id)}>{t('knowledge.web.pages')}</Button>
              {s.read_finished_at && (
                <Button size="xs" variant="default" disabled={busy(s)} loading={readAgain.isPending && readAgain.variables?.id === s.id} onClick={() => readAgain.mutate(s)}>
                  {t('knowledge.web.readAgain')}
                </Button>
              )}
              <ActionIcon
                variant="subtle" size="sm" color="error"
                aria-label={t('knowledge.web.remove', { name: s.name })}
                onClick={() => openConfirmModal({
                  title: t('knowledge.web.removeTitle'),
                  children: <Text role="bodySmall">{t('knowledge.web.removeBody', { name: s.name, count: s.documents })}</Text>,
                  labels: { confirm: t('action.delete'), cancel: t('action.cancel') },
                  confirmProps: { color: 'red' },
                  onConfirm: () => remove.mutate(s)
                })}
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M2.8 4.2h10.4M6.2 4.2V2.8h3.6v1.4M4.2 4.2l.6 9h6.4l.6-9M6.6 6.8v4M9.4 6.8v4" />
                </svg>
              </ActionIcon>
            </Group>
          </div>
        ))}
      </Stack>
      <WebsitePages id={open} onClose={() => setOpen(null)} onChanged={refresh} />
    </Card>
  );
}

/** The pages found on one website, the suggestion, and the choice. */
function WebsitePages({ id, onClose, onChanged }: { id: string | null; onClose: () => void; onChanged: () => void }) {
  const t = useT();
  const site = useQuery({
    queryKey: ['kb-website', id],
    queryFn: () => flows.getKbWebsite(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.data && busy(q.state.data.data) ? 3000 : false)
  });
  const s = site.data?.data;
  const pages = useMemo(() => s?.pages ?? [], [s]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [shownFor, setShownFor] = useState<string | null>(null);
  useEffect(() => {
    // The choice is taken from the server once a site's analysis is in, and kept while the person edits it.
    if (!s || busy(s)) return;
    const key = `${s.id}:${s.analysed_at ?? ''}:${s.read_finished_at ?? ''}`;
    if (key === shownFor) return;
    setChosen(new Set(s.selected ?? []));
    setShownFor(key);
  }, [s, shownFor]);
  const room = s?.analysis.room;
  const over = room != null && chosen.size > room;

  const read = useMutation({
    mutationFn: async () => {
      await flows.selectKbWebsitePages(s!.id, { urls: [...chosen] });
      return flows.readKbWebsite(s!.id);
    },
    onSuccess: () => { onChanged(); void site.refetch(); notifySuccess(t('knowledge.web.toast.reading'), s?.name ?? ''); },
    onError: (e) => notifyError(errorText(e, t('common.unknownError')), s?.name ?? '')
  });
  const toggle = (url: string, on: boolean) => setChosen((c) => { const n = new Set(c); if (on) n.add(url); else n.delete(url); return n; });
  const worth = pages.filter((p) => !p.reason || p.reason === 'no_room');
  const others = pages.filter((p) => p.reason && p.reason !== 'no_room');
  const counts = s?.last_counts ?? {};

  return (
    <Drawer opened={!!id} onClose={onClose} title={s?.name ?? t('knowledge.web.title')} position="right" size="xl">
      <Stack gap="md" data-testid="kb-website-pages">
        {s && busy(s) && <Alert color="info" role="status">{t(s.job === 'analyse' || s.status === 'analysing' ? 'knowledge.web.busy.analysing' : 'knowledge.web.busy.reading')}</Alert>}
        {s && !busy(s) && s.analysed_at && (
          <Alert color={over ? 'warning' : 'info'}>
            <Stack gap={4}>
              <Text role="bodySmall" fw={600}>
                {room == null
                  ? t('knowledge.web.suggest.unlimited', { worth: formatCount(s.analysis.worth ?? 0), suggested: formatCount(s.analysis.suggested ?? 0) })
                  : t('knowledge.web.suggest.room', { worth: formatCount(s.analysis.worth ?? 0), room: formatCount(room), suggested: formatCount(s.analysis.suggested ?? 0) })}
              </Text>
              <Text role="caption" c={MUTED}>{t('knowledge.web.suggest.why')}</Text>
              {s.analysis.complete === false && <Text role="caption" c={MUTED}>{t('knowledge.web.suggest.incomplete')}</Text>}
              {over && <Text role="caption">{t('knowledge.web.overRoom', { room: formatCount(room ?? 0) })}</Text>}
            </Stack>
          </Alert>
        )}
        {s?.read_finished_at && (
          <Text role="bodySmall" c={MUTED}>
            {t('knowledge.web.lastCounts', {
              added: counts.added ?? 0, updated: counts.updated ?? 0, unchanged: counts.unchanged ?? 0, removed: counts.removed ?? 0, failed: counts.failed ?? 0
            })}
          </Text>
        )}
        {worth.length > 0 && (
          <Stack gap={6}>
            <Group justify="space-between">
              <Text role="label">{t('knowledge.web.worthTitle', { count: formatCount(worth.length) })}</Text>
              <Group gap={4}>
                <Button size="xs" variant="subtle" onClick={() => setChosen(new Set(worth.filter((p) => p.suggested).map((p) => p.url)))}>{t('knowledge.web.useSuggestion')}</Button>
                <Button size="xs" variant="subtle" onClick={() => setChosen(new Set())}>{t('knowledge.web.clear')}</Button>
              </Group>
            </Group>
            {worth.map((p) => (
              <div key={p.url} className="rr-knowledge__page" data-testid="kb-website-page">
                <Checkbox
                  checked={chosen.has(p.url)}
                  onChange={(v) => toggle(p.url, v)}
                  label={
                    <span>
                      <strong>{p.title || p.url}</strong>{p.new ? ` · ${t('knowledge.web.new')}` : ''}{p.suggested && !chosen.has(p.url) ? ` · ${t('knowledge.web.suggested')}` : ''}
                      <br />
                      <span style={{ color: MUTED, fontSize: 12 }}>{p.url} · {t('knowledge.web.words', { count: formatCount(p.words) })}</span>
                    </span>
                  }
                />
              </div>
            ))}
          </Stack>
        )}
        {others.length > 0 && (
          <details>
            <summary style={{ fontSize: 14 }}>{t('knowledge.web.othersTitle', { count: formatCount(others.length) })}</summary>
            <Stack gap={4} mt="xs">
              {others.map((p) => (
                <Text key={p.url} role="caption" c={MUTED}>{p.title || p.url} · {t(`knowledge.web.reason.${p.reason}`)}</Text>
              ))}
            </Stack>
          </details>
        )}
        <Text role="caption" c={MUTED}>{t('knowledge.web.jsHint')}</Text>
        <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
          <Button variant="default" onClick={onClose}>{t('action.close')}</Button>
          <Button loading={read.isPending} disabled={!s || busy(s) || chosen.size === 0 || over} onClick={() => read.mutate()}>
            {t(s?.read_finished_at ? 'knowledge.web.readChosenAgain' : 'knowledge.web.readChosen', { count: chosen.size })}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
